import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, ArrowRight, Phone } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MarkdownContent } from '../components/MarkdownContent';
import { QuoteForm } from '../components/QuoteForm';
import { FloatingContact } from '../components/FloatingContact';
import { getServiceBySlug, getCompanyInfo, getAreas } from '../api/api';
import { useSeo } from '../hooks/useSeo';

const SITE = 'https://www.nettoplus.be';

export const ServicePage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [company, setCompany] = useState(null);
  const [zones, setZones] = useState([]);
  const [etat, setEtat] = useState('chargement');

  useEffect(() => {
    let actif = true;
    setEtat('chargement');
    window.scrollTo(0, 0);

    Promise.all([getServiceBySlug(slug), getCompanyInfo(), getAreas()])
      .then(([s, c, z]) => {
        if (!actif) return;
        setService(s);
        setCompany(c);
        setZones(z);
        setEtat('ok');
      })
      .catch(() => actif && setEtat('erreur'));

    return () => {
      actif = false;
    };
  }, [slug]);

  // Titre, description, URL canonique, image de partage et données structurées
  // propres à cette page de service — ce que Google affiche dans ses résultats,
  // rattache à la bonne adresse, et comprend comme une prestation locale.
  const villeSeo = company?.city || 'Liège';
  const canonical = `${SITE}/services/${slug}`;
  const ldGraph = service
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Service',
            serviceType: service.title,
            name: `${service.title} à ${villeSeo}`,
            description: service.page_intro || service.description,
            ...(service.image ? { image: service.image } : {}),
            areaServed: { '@type': 'City', name: villeSeo },
            provider: {
              '@type': 'LocalBusiness',
              name: company?.name || 'NETTO+',
              ...(company?.phone ? { telephone: company.phone } : {}),
              ...(company?.address ? { address: company.address } : {}),
              url: `${SITE}/`,
            },
            url: canonical,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${SITE}/` },
              { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE}/services` },
              { '@type': 'ListItem', position: 3, name: service.title, item: canonical },
            ],
          },
        ],
      }
    : undefined;
  useSeo({
    title: service && company ? `${service.title} à ${villeSeo} | ${company.name}` : undefined,
    description: service ? (service.page_intro || service.description) : undefined,
    path: `/services/${slug}`,
    image: service?.image,
    jsonLd: ldGraph,
  });

  if (etat === 'chargement') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 max-w-4xl mx-auto px-4 pt-32 pb-20 w-full animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-3/4" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
        <Footer />
      </div>
    );
  }

  if (etat === 'erreur' || !service) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center px-4 pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Service introuvable</h1>
          <Link to="/" className="text-blue-600 underline">
            Voir tous nos services
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ville = company?.city || 'Liège';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1">
        <nav aria-label="Fil d'Ariane" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-blue-600">
                Accueil
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/services" className="hover:text-blue-600">
                Services
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-900 font-medium">{service.title}</li>
          </ol>
        </nav>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                {service.title} à {ville}
              </h1>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                {service.page_intro || service.description}
              </p>

              {service.bullet_points?.length > 0 && (
                <ul className="mt-6 space-y-3">
                  {service.bullet_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#devis"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Demander un devis gratuit
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </a>
                {company?.phone && (
                  <a
                    href={`tel:${company.phone}`}
                    className="inline-flex items-center gap-2 border-2 border-gray-200 hover:border-blue-600 px-6 py-3 rounded-lg font-semibold text-gray-900 transition-colors"
                  >
                    <Phone className="w-4 h-4" aria-hidden="true" />
                    {company.phone}
                  </a>
                )}
              </div>
            </div>

            {service.image && (
              <img
                src={service.image}
                alt={`${service.title} à ${ville}`}
                width="800"
                height="600"
                loading="eager"
                className="rounded-2xl shadow-lg w-full h-auto object-cover aspect-[4/3]"
              />
            )}
          </div>
        </section>

        {service.page_content && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <MarkdownContent content={service.page_content} />
          </section>
        )}

        {zones.length > 0 && (
          <section className="bg-gray-50 py-12">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {service.title} : nos zones d'intervention
              </h2>
              <ul className="flex flex-wrap gap-2">
                {zones.map((zone) => (
                  <li key={zone.id}>
                    <Link
                      to={`/zones/${zone.slug}`}
                      className="inline-block bg-white border border-gray-200 hover:border-blue-600 hover:text-blue-600 px-4 py-2 rounded-full text-sm text-gray-700 transition-colors"
                    >
                      {zone.name}
                      {zone.postal_code && (
                        <span className="text-gray-400 ml-1">{zone.postal_code}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <div id="devis">
          <QuoteForm serviceParDefaut={service.title} />
        </div>
      </main>

      <Footer />
      <FloatingContact />
    </div>
  );
};

export default ServicePage;
